/**
 * @file AntiBodyAntiHBsListView.jsx
 * @author Daniel Aguirre
 * @description Dashboard Detail Management View Page
 */

// Dependencies
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Helpers
import { useParams } from 'react-router';
import {
  formatDate,
  joinArrayObject,
  removeNullValuesFromObject,
  sortStringPlus
} from 'commons/helpers/utils';
import {
  createColumns,
  getSearchApiKeys,
  getSorterString,
  getSelectFilter,
  PAGE_LIMIT
} from '../helper/utils';
import DashboardDetailList from '../dashboardDetailList';

function AntiBodyAntiHBsListView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const [rowData, setRowData] = React.useState([]);
  const { classification, totalItems: totalItemsParams } = useParams();
  const [totalItems, setTotalItems] = useState(totalItemsParams);
  const [dataFilter, setDataFilter] = useState(null);
  const [columns, setColumns] = useState([]);
  const [filter, setFilter] = useState(null);
  const [sorter, setSorter] = useState(null);

  const { getAntibodyAntiHBsListPlus } = dashboardStaffActions;

  const additionalColumns = [
    {
      title: translate('dashboardStaff.serostateClasification'),
      dataIndex: 'clasificacion_seroestado_hepatitisb',
      filter: {
        listName: 'lista_clasificacion_seroestado_hepatitis_b',
        searchName: 'filtroClasificacionSeroestadoHepatitisB',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('clasificacion_seroestado_hepatitisb'),
        multiple: 16
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_clasificacion_seroestado_hepatitis_b',
            searchName: 'filtroClasificacionSeroestadoHepatitisB'
          },
          dataIndex: 'clasificacion_seroestado_hepatitisb'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.vaccinationSchedule'),
      dataIndex: 'esquema_hepatitisb',
      filter: {
        listName: 'lista_esquema_vacunacion',
        searchName: 'filtroEsquemaVacunacion',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('esquema_hepatitisb'),
        multiple: 17
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_esquema_vacunacion',
            searchName: 'filtroEsquemaVacunacion'
          },
          dataIndex: 'esquema_hepatitisb'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.dateLastAntiHBs'),
      dataIndex: 'fecha_ultimo_antihbs',
      filter: {
        listName: 'lista_fecha_ultimo_antihbs',
        searchName: 'filtroFechaUltimoAntihbs',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('fecha_ultimo_antihbs'),
        multiple: 18
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_fecha_ultimo_antihbs',
            searchName: 'filtroFechaUltimoAntihbs'
          },
          dataIndex: 'fecha_ultimo_antihbs'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.resultLastAntiHBs'),
      dataIndex: 'ultimo_antihbs',
      filter: {
        listName: 'lista_ultimo_antihbs',
        searchName: 'filtroUltimoAntihbs',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('ultimo_antihbs'),
        multiple: 19
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_ultimo_antihbs',
            searchName: 'filtroUltimoAntihbs'
          },
          dataIndex: 'ultimo_antihbs'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.treatmentOptions'),
      dataIndex: 'opciones_tratamiento',
      filter: {
        listName: 'lista_opciones_tratamiento',
        searchName: 'filtroOpcionesTratamiento',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('opciones_tratamiento'),
        multiple: 20
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_opciones_tratamiento',
            searchName: 'filtroOpcionesTratamiento'
          },
          dataIndex: 'opciones_tratamiento'
        },
        dataFilter
      ),
      ellipsis: true
    }
  ];

  const getetData = (offset, pageLimit) => {
    const pageSize = pageLimit || PAGE_LIMIT;
    const joinFilter = joinArrayObject(filter);
    const joinSorter = joinArrayObject(sorter);
    const data = {
      params: {
        clasificacionSeleccionada: classification,
        idClinic: clinicId,
        limit: pageSize,
        offset,
        ...joinFilter,
        ...joinSorter
      }
    };
    getAntibodyAntiHBsListPlus(data, (_, resp) => {
      if (resp?.data && resp.data?.totalRegistros != null) {
        const newRowData = resp?.data?.listaDetalles?.map((item) => {
          return {
            ...item,
            fecha_ultimo_antihbs: item?.fecha_ultimo_antihbs
              ? formatDate(item.fecha_ultimo_antihbs)
              : ''
          };
        });

        setTotalItems(resp.data?.totalRegistros);
        setRowData(newRowData ?? []);
        setDataFilter(resp.data);
      }
    });
  };

  useEffect(() => {
    if (clinicId !== null) getetData(1);
  }, [clinicId]);

  useEffect(() => {
    const cols = createColumns(translate, dataFilter);
    setColumns(cols);
  }, [rowData, dataFilter]);

  const handleTableChange = (pagination, filters, sorterFilter) => {
    if (filters || sorterFilter) {
      const convertedFilters = getSearchApiKeys(
        [...columns, ...additionalColumns],
        removeNullValuesFromObject(filters)
      );
      const convertedSorts = getSorterString(sorterFilter);
      setFilter(convertedFilters);
      setSorter(convertedSorts);
    }
  };
  return (
    <DashboardDetailList
      columns={columns}
      rowData={rowData}
      totalItems={totalItems}
      onPaginationChange={getetData}
      additionalColumns={additionalColumns}
      onChange={handleTableChange}
      filter={filter}
      sorter={sorter}
    />
  );
}

/**
 * Component definition of proptypes.
 */
AntiBodyAntiHBsListView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getAntibodyAntiHBsListPlus: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default AntiBodyAntiHBsListView;
